import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { gracefullyDecompressJson } from '../../../compression/lazyStringCompression';
import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS, getMaxDataChannelMessageSizeBytes } from '../../../config';
import { logger } from '../../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../../events';
import { debounce, gracefullyParseJson } from '../../../utils';
import { DataChannelMessage } from '../../../video-sdk/core/message/types';
import { RemoteDataComponentProps } from '../types';
import { dataChannelAppStateAtom } from './atoms';

const sendDebouncedLoggingEvents = debounce(sendLoggingEvents, DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS);

async function gracefullyParseMessage(
  message: string,
): Promise<[DataChannelMessage, null] | [null, Error] | [null, null]> {
  const maxMessageSizeBytes = getMaxDataChannelMessageSizeBytes();
  return maxMessageSizeBytes
    ? await gracefullyDecompressJson<DataChannelMessage>(message)
    : [gracefullyParseJson<DataChannelMessage>(message), null];
}

export function useMessageEventHandlerCallback({
  onMessageReceived,
}: Pick<RemoteDataComponentProps, 'onMessageReceived'>) {
  const getDataTrackState = useAtomCallback(useCallback((get) => get(dataChannelAppStateAtom), []));

  return useCallback(
    async (message: string) => {
      const [parsedMessage, error] = await gracefullyParseMessage(message);
      if (!parsedMessage) {
        logger.error(
          'Unable to parse received message',
          {
            message,
          },
          ...(error ? [error] : []),
        );
        return null;
      }

      const participantState = getDataTrackState();

      sendDebouncedLoggingEvents(
        TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_RECEIVED,
        {
          isRecording: participantState.isRecording,
          recordingState: participantState.recordingState,
          patientConsent: participantState.patient.consent,
          providerConsent: participantState.provider.consent,
          isWhiteboardMode: participantState.isWhiteboardMode,
        },
        {
          logLevel: 'info',
          message: 'Received a message on the data track (DEBOUNCED)',
        },
      );
      onMessageReceived?.(parsedMessage);
    },
    [onMessageReceived, getDataTrackState],
  );
}
